import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Switch, 
  Button, 
  Typography, 
  Alert, 
  List, 
  Divider,
  Row,
  Col,
  Space,
  message
} from 'antd';
import { 
  NotificationOutlined, 
  MailOutlined, 
  MobileOutlined, 
  SecurityScanOutlined, 
  LockOutlined, 
  UserOutlined, 
  WarningOutlined,
  CheckOutlined,
  CloseOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { 
  getSecurityNotificationSettings, 
  updateSecurityNotificationSettings 
} from '../../../services/securitySettings';

const { Title, Text } = Typography;

const SecurityNotifications = () => {
  const [formData, setFormData] = useState({
    login_notifications: true,
    failed_login_attempts: true,
    password_changes: true,
    profile_updates: true,
    admin_alerts: true,
    email_notifications: true,
    push_notifications: false,
    updated_by: localStorage.getItem('userId') || ''
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  
  // Fetch security notification settings on component mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true);
        const settings = await getSecurityNotificationSettings();
        setFormData(prev => ({
          ...prev,
          ...settings,
          updated_by: localStorage.getItem('userId') || ''
        }));
        setError(null);
      } catch (err) {
        console.error('Failed to load security notification settings:', err);
        setError('Failed to load security notification settings. Please try again later.');
        // Set default values on error
        setFormData({
          ...formData,
          updated_by: localStorage.getItem('userId') || ''
        });
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);
  
  const handleChange = (e) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: checked
    }));
  };
  
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setSaving(true);
    
    try {
      // Prepare the data to send (exclude any non-settings fields if needed)
      const { updated_by, ...settingsToUpdate } = formData;
      
      // Call the API to update settings
      const updatedSettings = await updateSecurityNotificationSettings({
        ...settingsToUpdate,
        // updated_by: localStorage.getItem('userId') || ''
      });
      
      // Update the form with the returned settings (in case the server made adjustments)
      setFormData(prev => ({
        ...prev,
        ...updatedSettings,
        updated_by: localStorage.getItem('userId') || ''
      }));
      
      message.success('Security notification settings updated successfully');
      setError(null);
    } catch (err) {
      console.error('Failed to update security notification settings:', err);
      setError('Failed to update security notification settings. Please try again.');
      message.error('Failed to update settings');
    } finally {
      setSaving(false);
    }
  };
  
  const handleRefresh = async () => {
    try {
      setLoading(true);
      const settings = await getSecurityNotificationSettings();
      setFormData(prev => ({
        ...prev,
        ...settings,
        updated_by: localStorage.getItem('userId') || ''
      }));
      message.success('Settings refreshed');
      setError(null);
    } catch (err) {
      console.error('Failed to refresh settings:', err);
      message.error('Failed to refresh settings');
    } finally {
      setLoading(false);
    }
  };
  
  const notificationTypes = [
    {
      name: 'Login Notifications',
      key: 'login_notifications',
      icon: <NotificationOutlined />,
      description: 'Receive notifications when users log in from new devices or locations.'
    },
    {
      name: 'Failed Login Attempts',
      key: 'failed_login_attempts',
      icon: <WarningOutlined style={{ color: '#ff4d4f' }} />,
      description: 'Get alerted about multiple failed login attempts which may indicate a brute force attack.'
    },
    {
      name: 'Password Changes',
      key: 'password_changes',
      icon: <LockOutlined />,
      description: 'Be notified when users change their passwords.'
    },
    {
      name: 'Profile Updates',
      key: 'profile_updates',
      icon: <UserOutlined />,
      description: 'Receive notifications when sensitive profile information is updated.'
    },
    {
      name: 'Admin Security Alerts',
      key: 'admin_alerts',
      icon: <SecurityScanOutlined style={{ color: '#faad14' }} />,
      description: 'Important security-related alerts for administrators.'
    }
  ];
  
  const notificationMethods = [
    {
      name: 'Email Notifications',
      key: 'email_notifications',
      icon: <MailOutlined />,
      description: 'Receive security notifications via email.'
    },
    {
      name: 'Push Notifications',
      key: 'push_notifications',
      icon: <MobileOutlined />,
      description: 'Receive security notifications as push notifications on your devices.'
    }
  ];
  
  if (loading) {
    return (
      <Card title="Security Notifications" bordered={false}>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
          <div className="ant-spin ant-spin-lg" />
        </div>
      </Card>
    );
  }
  
  return (
    <Card 
      title="Security Notifications" 
      bordered={false}
      extra={
        <Space>
          <Button 
            icon={<ReloadOutlined />}
            onClick={handleRefresh}
            disabled={saving || loading}
          >
            Refresh
          </Button>
          <Button 
            type="primary" 
            icon={<CheckOutlined />}
            onClick={handleSubmit}
            loading={saving}
            disabled={saving || loading}
          >
            Save Settings
          </Button>
        </Space>
      }
    >
      <Text type="secondary">Configure security alerts and notifications</Text>
      
      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          action={
            <Button size="small" type="text" onClick={() => setError(null)}>
              <CloseOutlined />
            </Button>
          }
        />
      )}
      
      <Divider />
      
      <Row gutter={[24, 24]}>
        <Col xs={24} md={12}>
          <div className="mb-4">
            <Title level={5}>Notification Types</Title>
            <Text type="secondary">Select which security events should trigger notifications.</Text>
          </div>
          
          <Card className="mb-4" bodyStyle={{ padding: 0 }}>
            <List
              itemLayout="horizontal"
              dataSource={notificationTypes}
              renderItem={(notification) => (
                <List.Item
                  key={notification.key}
                  className="security-notification-item"
                  actions={[
                    <div className="notification-action">
                      <Switch 
                        checked={formData[notification.key]} 
                        onChange={(checked) => handleChange({ target: { name: notification.key, checked } })}
                        checkedChildren="On" 
                        unCheckedChildren="Off"
                        className="notification-switch"
                      />
                    </div>
                  ]}
                >
                  <List.Item.Meta
                    avatar={
                      <div className="notification-icon-wrapper" style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '40px',
                        height: '40px',
                      }}>
                        {React.cloneElement(notification.icon, { 
                          style: { 
                            fontSize: '24px', 
                            color: '#faad14',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          } 
                        })}
                      </div>
                    }
                    title={
                      <div className="notification-title" style={{ fontSize: '16px' }}>
                        {notification.name}
                      </div>
                    }
                    description={
                      <div className="notification-description">
                        {notification.description}
                      </div>
                    }
                    className="notification-meta"
                  />
                </List.Item>
              )}
            />
          </Card>
        </Col>
        
        <Col xs={24} md={12}>
          <div className="mb-4">
            <Title level={5}>Notification Methods</Title>
            <Text type="secondary">Choose how you want to receive security notifications.</Text>
          </div>
          
          <Card className="mb-4" bodyStyle={{ padding: 0 }}>
            <List
              itemLayout="horizontal"
              dataSource={notificationMethods}
              renderItem={(method) => (
                <List.Item
                  key={method.key}
                  actions={[
                    <Switch 
                      checked={formData[method.key]} 
                      onChange={(checked) => handleChange({ target: { name: method.key, checked } })}
                      checkedChildren="On" 
                      unCheckedChildren="Off"
                    />
                  ]}
                >
                  <List.Item.Meta
                    
                    avatar={
                      <div className="notification-icon-wrapper" style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '40px',
                        height: '40px',
                      }}>
                        {React.cloneElement(method.icon, { 
                          style: { 
                            fontSize: '24px', 
                            color: '#faad14',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }} 
                        )}
                      </div>
                    }
                    title={
                      <div className="notification-title" style={{ fontSize: '16px' }}>
                        {method.name}
                      </div>
                    }
                    description={
                      <div className="notification-description">
                        {method.description}
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
          </Card>
          
          <Alert 
            message="Note" 
            description={
              <Text>
                Email notifications require a valid email address in your profile.
                Push notifications require the mobile app to be installed and configured.
              </Text>
            } 
            type="info" 
            showIcon
            className="mb-4"
          />
        </Col>
      </Row>
    </Card>
  );
};

export default SecurityNotifications;
